/**
 * Shared zoom + comments controls for PDF and image viewer modals.
 */

import React, { useCallback, useState } from 'react';
import {
  VIEWER_ZOOM_MAX,
  VIEWER_ZOOM_MIN,
} from '../../utils/viewerZoom';
import { ZoomIn, ZoomOut, MessageSquare, Download } from 'lucide-react';

/** Safe filename from a document title + extension (e.g. `.pdf`). */
export function buildDownloadFilename(title: string, extWithDot: string): string {
  const ext = extWithDot.startsWith('.') ? extWithDot : `.${extWithDot}`;
  const stripped = title.replace(/[/\\?%*:|"<>]/g, '').replace(/\s+/g, ' ').trim() || 'download';
  const lower = stripped.toLowerCase();
  if (lower.endsWith(ext.toLowerCase())) return stripped;
  return `${stripped}${ext}`;
}

async function triggerBrowserDownload(href: string, filename: string): Promise<void> {
  try {
    const res = await fetch(href, { mode: 'cors', credentials: 'omit' });
    if (!res.ok) throw new Error(String(res.status));
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objectUrl);
  } catch {
    try {
      const a = document.createElement('a');
      a.href = href;
      a.download = filename;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      window.open(href, '_blank', 'noopener,noreferrer');
    }
  }
}

export interface ViewerToolbarProps {
  displayScale: number;
  fitMode: boolean;
  commentsOpen: boolean;
  annotationCount: number;
  onCommentsOpen: () => void;
  onZoomOut: () => void;
  onZoomIn: () => void;
  onZoomToFit: () => void;
  minScale?: number;
  maxScale?: number;
  /** Extra controls (e.g. page pager) rendered after Fit */
  trailing?: React.ReactNode;
  /** When set, shows a download control after `trailing` (e.g. far right of the footer). */
  downloadUrl?: string;
  downloadFilename?: string;
}

export function ViewerToolbar({
  displayScale,
  fitMode,
  commentsOpen,
  annotationCount,
  onCommentsOpen,
  onZoomOut,
  onZoomIn,
  onZoomToFit,
  minScale = VIEWER_ZOOM_MIN,
  maxScale = VIEWER_ZOOM_MAX,
  trailing,
  downloadUrl,
  downloadFilename = 'download',
}: ViewerToolbarProps) {
  const [downloadBusy, setDownloadBusy] = useState(false);

  const handleDownload = useCallback(async () => {
    if (!downloadUrl) return;
    setDownloadBusy(true);
    try {
      await triggerBrowserDownload(downloadUrl, downloadFilename);
    } finally {
      setDownloadBusy(false);
    }
  }, [downloadUrl, downloadFilename]);

  return (
    <div className="pdf-viewer-toolbar">
      <button
        type="button"
        className={`pdf-viewer-toolbar-btn pdf-viewer-comments-btn ${commentsOpen ? 'active' : ''}`}
        onClick={onCommentsOpen}
        title="View comments"
        aria-label="View comments"
      >
        <MessageSquare size={18} />
        {annotationCount > 0 && <span className="pdf-viewer-comments-badge">{annotationCount}</span>}
      </button>
      <button type="button" onClick={onZoomOut} disabled={displayScale <= minScale} aria-label="Zoom out">
        <ZoomOut size={18} />
      </button>
      <span className="pdf-viewer-zoom-value">{Math.round(displayScale * 100)}%</span>
      <button type="button" onClick={onZoomIn} disabled={displayScale >= maxScale} aria-label="Zoom in">
        <ZoomIn size={18} />
      </button>
      <button
        type="button"
        className={`pdf-viewer-toolbar-btn ${fitMode ? 'active' : ''}`}
        onClick={onZoomToFit}
        title="Fit to window"
      >
        Fit
      </button>
      {trailing}
      {downloadUrl ? (
        <button
          type="button"
          className="pdf-viewer-toolbar-btn pdf-viewer-toolbar-download"
          onClick={() => void handleDownload()}
          disabled={downloadBusy}
          title="Download file"
          aria-label="Download file"
        >
          <Download size={18} />
        </button>
      ) : null}
    </div>
  );
}
